<template>
  <div class="rule-condition-form">
    <div v-if="value.length === 0" class="empty-text">
      {{ $t('CenterForSorting.noRuleConditions') }}
    </div>
    <div v-for="(condition, index) in value" :key="index" class="condition-row">
      <el-row
        :gutter="5" type="flex" align="middle"
        style="width: 100%;"
      >
        <el-col :span="1" style="min-width: 30px; text-align: center;">
          <span class="row-index">{{ index + 1 }}.</span>
        </el-col>
        <el-col :span="5">
          <el-form-item
            :prop="`${propPrefix}.${index}.conditionType`"
            :rules="{ required: true, message: $t('CenterForSorting.pleaseChoose'), trigger: 'change' }"
            class="condition-item"
            label-width="0"
          >
            <el-select
              v-model="condition.conditionType"
              :placeholder="$t('CenterForSorting.conditionType')"
              size="small"
              style="width: 100%;"
              :disabled="disabled || (isUpdateEnabled && condition.isOriginal)"
              @change="(val) => handleConditionTypeChange(val, index)"
            >
              <el-option
                v-for="config in conditionConfigs"
                :key="config.conditionType"
                :disabled="config.conditionType==='DEFAULT' || config.conditionType==='MACHINE_FALLBACK'"
                :label="$t('CenterForSorting.' + config.conditionType)"
                :value="config.conditionType"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item
            :prop="`${propPrefix}.${index}.operator`"
            :rules="{ required: true, message: $t('CenterForSorting.pleaseChoose'), trigger: 'change' }"
            class="condition-item"
            label-width="0"
          >
            <el-select
              v-model="condition.operator"
              :placeholder="$t('CenterForSorting.operator')"
              size="small"
              style="width: 100%;"
              :disabled="disabled || (isUpdateEnabled && condition.isOriginal)"
              @change="(val) => handleOperatorChange(val, index)"
            >
              <el-option
                v-for="op in getOperators(condition.conditionType)"
                :key="op"
                :label="$t('CenterForSorting.' + op)"
                :value="op"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="13">
          <el-form-item
            :prop="`${propPrefix}.${index}.conditionValue`"
            :rules="{ required: true, message: $t('CenterForSorting.notNull'), trigger: ['blur', 'change'] }"
            class="condition-item"
            label-width="0"
          >
            <div style="width: 100%;">
              <template v-if="getValueType(condition.conditionType) === 'ZipRange'">
                <zip-range
                  v-model="condition.conditionValue"
                  :disabled="disabled || (isUpdateEnabled && condition.isOriginal)"
                />
              </template>
              <template v-else-if="getValueType(condition.conditionType) === 'Radio'">
                <el-radio-group v-model="condition.conditionValue" size="small" :disabled="disabled || (isUpdateEnabled && condition.isOriginal)">
                  <el-radio
                    v-for="opt in getAllowedValues(condition.conditionType)"
                    :key="opt.value"
                    :label="String(opt.value)"
                    :disabled="opt.disabled"
                  >
                    {{ opt.label }}
                  </el-radio>
                </el-radio-group>
              </template>
              <template v-else-if="getValueType(condition.conditionType) === 'InputNumber'">
                <el-input-number
                  v-model="condition.conditionValue" :min="1" size="small"
                  :disabled="disabled || (isUpdateEnabled && condition.isOriginal)"
                />
              </template>
              <template v-else-if="getValueType(condition.conditionType) === 'Select'">
                <el-select
                  :value="getConditionValue(condition)"
                  :placeholder="$t('CenterForSorting.conditionValue')"
                  filterable
                  :multiple="isMultiple(condition)"
                  collapse-tags
                  size="small"
                  style="width: 100%;"
                  :disabled="disabled || (isUpdateEnabled && condition.isOriginal)"
                  @change="(val) => setConditionValue(val, condition)"
                >
                  <el-option
                    v-for="opt in getAllowedValues(condition.conditionType)"
                    :key="opt.value"
                    :label="opt.label"
                    :value="String(opt.value)"
                    :disabled="opt.disabled"
                  />
                </el-select>
              </template>
              <template v-else>
                <el-input
                  v-model="condition.conditionValue" :placeholder="$t('CenterForSorting.conditionValue')" size="small"
                  style="width: 100%;"
                  :disabled="disabled || (isUpdateEnabled && condition.isOriginal)"
                />
              </template>
            </div>
          </el-form-item>
        </el-col>
        <el-col :span="1" style="text-align: center;">
          <el-button
            type="danger"
            icon="el-icon-minus"
            circle
            size="mini"
            style="margin-bottom: 18px;"
            :disabled="disabled || (isUpdateEnabled && condition.isOriginal)"
            @click="removeCondition(index)"
          />
        </el-col>
      </el-row>
    </div>

    <div class="add-btn-wrapper">
      <el-button
        type="primary" plain icon="el-icon-plus"
        size="small" :disabled="disabled" @click="addCondition"
      >
        {{ $t('CenterForSorting.addCondition') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import ZipRange from './ZipRange';

export default {
  name: 'RuleConditionForm',
  components: {
    ZipRange
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    conditionConfigs: {
      type: Array,
      default: () => []
    },
    propPrefix: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isUpdateEnabled: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    getOperators(type) {
      const config = this.conditionConfigs.find(c => c.conditionType === type);
      return config ? config.operators : [];
    },
    getValueType(type) {
      const config = this.conditionConfigs.find(c => c.conditionType === type);
      return config ? config.valueType : 'Input';
    },
    getAllowedValues(type) {
      const config = this.conditionConfigs.find(c => c.conditionType === type);
      // 如果是数组全部加上disabled属性
      const allowedValues = config && Array.isArray(config.allowedValues) ? config.allowedValues : [];
      return allowedValues.map(item => ({
        ...item,
        disabled: this.isOptionDisabled(type, item)
      }));
    },
    /**
     * 判断选项是否disabled
     * @param type
     * @param option
     * @returns {boolean}
     */
    isOptionDisabled(type, option) {
      // return type === 'WAYBILL_STATUS' && String(option.value) === '13';
      return false;
    },
    handleConditionTypeChange(val, index) {
      const config = this.conditionConfigs.find(c => c.conditionType === val);
      if (config) {
        this.value[index].operator = config.operators[0] || '';
        this.value[index].conditionValue = ''; // 重置值

        // 如果是单选框且只有一个选项，默认选中
        if (config.valueType === 'Radio' && config.allowedValues && config.allowedValues.length === 1) {
          this.value[index].conditionValue = String(config.allowedValues[0].value);
        }
      }
    },
    handleOperatorChange(val, index) {
      // 当操作符改变时，如果导致多选/单选状态切换，或者仅仅为了安全起见，清空值
      // 为了用户体验，我们这里简单地清空值，因为操作符变了，原来的值通常不再适用（例如等于->包含）
      // 除非我们想做复杂的转换逻辑
      this.value[index].conditionValue = '';
    },
    // 判断是否为多选
    isMultiple(condition) {
      // 包含和不包含且类型为Select时开启多选
      const multipleOps = ['IN', 'NOT_IN'];
      return this.getValueType(condition.conditionType) === 'Select' && multipleOps.includes(condition.operator);
    },
    // 获取条件值（处理多选数组转换）
    getConditionValue(condition) {
      if (this.isMultiple(condition)) {
        if (condition.conditionValue === null || condition.conditionValue === undefined || condition.conditionValue === '') return [];
        return String(condition.conditionValue).split(',');
      }
      return condition.conditionValue;
    },
    // 设置条件值（处理多选数组转换）
    setConditionValue(val, condition) {
      if (Array.isArray(val)) {
        condition.conditionValue = val.join(',');
      } else {
        condition.conditionValue = val;
      }
    },
    addCondition() {
      this.value.push({
        conditionType: '',
        operator: '',
        conditionValue: ''
      });
    },
    removeCondition(index) {
      this.value.splice(index, 1);
    }
  }
};
</script>

<style scoped>
.rule-condition-form {
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}
.empty-text {
  color: #909399;
  font-size: 13px;
  text-align: center;
  padding: 10px 0;
}
.condition-row {
  margin-bottom: 0;
}
.row-index {
  color: #606266;
  font-weight: bold;
}
.condition-item {
  margin-bottom: 15px; /* Leave space for error message */
}
/* Reduce default el-form-item bottom margin */
.condition-item >>> .el-form-item__content {
  line-height: normal;
}
.add-btn-wrapper {
  margin-top: 5px;
}
</style>
